import React, { useState, useMemo } from 'react';
import {
    Card, Typography, Row, Col, Statistic, Select, Table, Spin, Space, Tag, Empty, Button,
    Progress, Avatar, Modal, Radio, message, Tooltip as AntTooltip
} from 'antd';
import {
    TeamOutlined, InfoCircleOutlined, FallOutlined, DownloadOutlined,
    CrownFilled, FileExcelOutlined, PrinterOutlined, FileTextOutlined, EyeOutlined
} from '@ant-design/icons';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { useAuth } from '../../../aws/AuthContext';
import dayjs from 'dayjs';
import ModalAnalisisVendedor from '../modals/ModalAnalisisVendedor';

const { Title, Text } = Typography;
const PALETA_COLORES = ['#22BABB', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#ec4899'];

const SeccionPersonal = ({ desde, hasta }) => {
    const { getJwtToken } = useAuth();
    const API_URL = process.env.REACT_APP_API_URL;

    const [empleadoSeleccionado, setEmpleadoSeleccionado] = useState('todos');

    // Estados para el Modal de Exportación
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [exportFormat, setExportFormat] = useState('xlsx');
    const [exportScope, setExportScope] = useState('todos');
    const [empleadoAnalizar, setEmpleadoAnalizar] = useState(null);

    const { data, isLoading, isError } = useQuery({
        queryKey: ['informesPersonal', desde, hasta],
        queryFn: async () => {
            const token = await getJwtToken();
            const response = await axios.get(`${API_URL}/v1-informes-personal`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { fecha_inicio: desde, fecha_fin: hasta, usuario_id: 'todos' }
            });
            return response.data;
        }
    });

    const rankingGlobal = data?.ranking || [];
    const kpisGlobales = data?.kpis || { total_ventas_periodo: 0, total_empleados_activos: 0 };

    const opcionesSelect = useMemo(() => [
        { value: 'todos', label: '👥 Todos los Empleados' },
        ...rankingGlobal.map(emp => ({ value: emp.id, label: emp.nombre_completo }))
    ], [rankingGlobal]);

    const rankingMostrado = useMemo(() => {
        if (empleadoSeleccionado === 'todos') return rankingGlobal;
        return rankingGlobal.filter(emp => emp.id === empleadoSeleccionado);
    }, [empleadoSeleccionado, rankingGlobal]);

    const datosDona = rankingGlobal.map(emp => ({ name: emp.nombre_completo, value: emp.total_vendido }));

    // ==========================================
    // LÓGICA DE EXPORTACIÓN (EXCEL, CSV, PDF)
    // ==========================================
    const handleExport = async () => {
        const dataAExportar = exportScope === 'todos' ? rankingGlobal : rankingMostrado;

        if (dataAExportar.length === 0) {
            return message.warning("No hay datos para exportar en el rango seleccionado.");
        }

        const tituloRango = `Del ${dayjs(desde).format('DD/MM/YYYY')} al ${dayjs(hasta).format('DD/MM/YYYY')}`;

        if (exportFormat === 'pdf') {
            // El navegador usa el @media print de CSS para imprimir la pantalla limpia
            setIsExportModalOpen(false);
            setTimeout(() => window.print(), 300);
            return message.success("Preparando documento PDF...");
        }

        if (exportFormat === 'csv') {
            const headers = ["Ranking,Empleado,Facturas,Ticket Promedio,Devoluciones,Total Vendido,Aporte %\n"];
            const rows = dataAExportar.map((r, i) =>
                `"${i + 1}","${r.nombre_completo}","${r.total_facturas}","${r.ticket_promedio}","${r.devoluciones}","${r.total_vendido}","${r.porcentaje_aporte}%"\n`
            );
            const csvContent = "\uFEFF" + headers.join("") + rows.join("");
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            saveAs(blob, `Rendimiento_Personal_${desde}.csv`);
            setIsExportModalOpen(false);
            return message.success("Archivo CSV descargado con éxito.");
        }

        if (exportFormat === 'xlsx') {
            try {
                const workbook = new ExcelJS.Workbook();
                const worksheet = workbook.addWorksheet('Rendimiento Personal');

                // 1. Ocultar líneas de cuadrícula para un look más limpio (estilo dashboard)
                worksheet.views = [{ showGridLines: false }];

                // 2. Establecer el ancho de las columnas ANTES de meter datos (sin usar la propiedad 'header')
                worksheet.getColumn('A').width = 12; // Posición
                worksheet.getColumn('B').width = 35; // Nombre
                worksheet.getColumn('C').width = 25; // Columna de apoyo para KPIs
                worksheet.getColumn('D').width = 20; // Facturas
                worksheet.getColumn('E').width = 22; // Ticket
                worksheet.getColumn('F').width = 20; // Devoluciones
                worksheet.getColumn('G').width = 22; // Vendido
                worksheet.getColumn('H').width = 15; // Aporte

                // 3. BLOQUE SUPERIOR IZQUIERDO (Simulación de Logo) - Filas 1 a 4
                worksheet.mergeCells('A1:B4');
                const logoCell = worksheet.getCell('A1');
                logoCell.value = 'YUGONUB\nPOS';
                logoCell.font = { name: 'Arial Black', size: 22, color: { argb: 'FF22BABB' }, bold: true };
                logoCell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };

                // 4. BLOQUE SUPERIOR DERECHO (KPIs Corporativos Oscuros) - Filas 1 a 4
                const kpisData = [
                    ['Reporte:', 'Rendimiento de Personal y Ventas'],
                    ['Rango de Fechas:', tituloRango],
                    ['Filtro Aplicado:', exportScope === 'todos' ? 'Equipo Completo' : dataAExportar[0]?.nombre_completo],
                    ['Fecha de Generación:', dayjs().format('DD/MM/YYYY hh:mm A')]
                ];

                kpisData.forEach((kpi, index) => {
                    const rowNum = index + 1;

                    // Celdas grises oscuras (Etiquetas)
                    worksheet.mergeCells(`C${rowNum}:E${rowNum}`);
                    const labelCell = worksheet.getCell(`C${rowNum}`);
                    labelCell.value = kpi[0];
                    labelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF334155' } }; // Gris oscuro
                    labelCell.font = { color: { argb: 'FFFFFFFF' }, bold: true };
                    labelCell.alignment = { horizontal: 'right', vertical: 'middle' };
                    labelCell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };

                    // Celdas blancas (Valores)
                    worksheet.mergeCells(`F${rowNum}:H${rowNum}`);
                    const valueCell = worksheet.getCell(`F${rowNum}`);
                    valueCell.value = kpi[1];
                    valueCell.font = { bold: true };
                    valueCell.alignment = { horizontal: 'center', vertical: 'middle' };
                    valueCell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
                });

                // Fila 5 queda vacía para dar respiro

                // 5. CABECERA DE LA TABLA (Fila 6)
                const headerRow = worksheet.getRow(6);
                headerRow.height = 25; // Hacemos la cabecera más ancha
                headerRow.values = [
                    'Ranking', 'Nombre del Empleado', 'Facturas Emitidas', '', 'Ticket Promedio', 'Devoluciones (Notas)', 'Total Vendido', 'Aporte (%)'
                ];

                // Arreglo visual por haber usado una columna de apoyo (C) en los KPIs
                worksheet.mergeCells('C6:D6');

                // Pintar la cabecera de la tabla
                ['A', 'B', 'C', 'E', 'F', 'G', 'H'].forEach(col => {
                    const cell = headerRow.getCell(col);
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF10B981' } }; // Verde agradable
                    cell.font = { color: { argb: 'FFFFFFFF' }, bold: true, size: 12 };
                    cell.alignment = { horizontal: 'center', vertical: 'middle' };
                    cell.border = { top: { style: 'medium', color: { argb: 'FF334155' } }, bottom: { style: 'medium', color: { argb: 'FF334155' } } };
                });

                // 6. LLENAR LOS DATOS (Fila 7 en adelante)
                dataAExportar.forEach((emp, index) => {
                    const row = worksheet.addRow([
                        index + 1,
                        emp.nombre_completo,
                        emp.total_facturas,
                        '', // Columna C vacía por el merge
                        emp.ticket_promedio,
                        emp.devoluciones,
                        emp.total_vendido,
                        emp.porcentaje_aporte / 100
                    ]);

                    worksheet.mergeCells(`C${row.number}:D${row.number}`);

                    // Formatear bordes tenues para cada fila de datos
                    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
                        cell.border = { bottom: { style: 'thin', color: { argb: 'FFEEEEEE' } } };
                        cell.alignment = { vertical: 'middle' };

                        // Centrar números simples
                        if (colNumber === 1 || colNumber === 3 || colNumber === 6) {
                            cell.alignment = { horizontal: 'center', vertical: 'middle' };
                        }
                    });

                    // Formatos financieros de Excel
                    row.getCell('E').numFmt = '"$"#,##0.00';
                    row.getCell('G').numFmt = '"$"#,##0.00';
                    row.getCell('H').numFmt = '0.00%';
                });

                // 7. Descargar archivo
                const buffer = await workbook.xlsx.writeBuffer();
                saveAs(new Blob([buffer]), `Reporte_Personal_${dayjs(desde).format('YYYYMMDD')}.xlsx`);
                setIsExportModalOpen(false);
                message.success("Archivo Excel Corporativo generado con éxito.");

            } catch (error) {
                console.error(error);
                message.error("Error al generar el archivo Excel.");
            }
        }
    };

    if (isLoading) return <div style={{ textAlign: 'center', padding: '100px 0' }}><Spin size="large" /></div>;
    if (isError) return <div style={{ textAlign: 'center', marginTop: '50px' }}><Text type="danger">Error al cargar datos.</Text></div>;

    const vistaIndividual = empleadoSeleccionado !== 'todos' && rankingMostrado.length > 0;
    const empleadoInfo = vistaIndividual ? rankingMostrado[0] : null;

    const ticketPromedioGlobal = rankingGlobal.length > 0
        ? rankingGlobal.reduce((acc, curr) => acc + curr.ticket_promedio, 0) / rankingGlobal.length
        : 0;
    const devolucionesGlobales = rankingGlobal.reduce((acc, curr) => acc + curr.devoluciones, 0);

    return (
        <div style={{ animation: 'fadeIn 0.5s' }} className="seccion-personal-container">
            {/* CSS PARA IMPRESIÓN (PDF) */}
            <style>{`
                @media print {
                    .no-print { display: none !important; }
                    .ant-card { box-shadow: none !important; border: 1px solid #e2e8f0 !important; page-break-inside: avoid; }
                    body { background: white !important; }
                }
            `}</style>

            <Row gutter={[16, 16]}>

                {/* COLUMNA IZQUIERDA: MÉTRICAS Y GRÁFICO */}
                <Col xs={24} xl={7} xxl={6}>
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>

                        <Card bordered={false} styles={{ body: { padding: '16px' } }} style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                            <Title level={5} style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#475569' }}>Métricas Generales</Title>
                            <Row gutter={[12, 16]}>
                                <Col span={12}>
                                    <Statistic title={<Text type="secondary" style={{ fontSize: '11px' }}>Ventas del Equipo</Text>} value={kpisGlobales.total_ventas_periodo} prefix="$" formatter={val => Number(val).toLocaleString('es-CO')} valueStyle={{ fontWeight: '700', color: '#059669', fontSize: '16px' }} />
                                </Col>
                                <Col span={12}>
                                    <Statistic title={<Text type="secondary" style={{ fontSize: '11px' }}>Personal Activo</Text>} value={kpisGlobales.total_empleados_activos} prefix={<TeamOutlined />} valueStyle={{ fontWeight: '700', color: '#0ea5e9', fontSize: '16px' }} />
                                </Col>
                                <Col span={12}>
                                    <Statistic title={<Text type="secondary" style={{ fontSize: '11px' }}>Ticket Promedio</Text>} value={ticketPromedioGlobal} prefix="$" formatter={val => Number(val).toLocaleString('es-CO', { maximumFractionDigits: 0 })} valueStyle={{ fontWeight: '700', color: '#475569', fontSize: '16px' }} />
                                </Col>
                                <Col span={12}>
                                    <Statistic title={<Text type="secondary" style={{ fontSize: '11px' }}>Devoluciones</Text>} value={devolucionesGlobales} prefix={<FallOutlined />} valueStyle={{ fontWeight: '700', color: '#ef4444', fontSize: '16px' }} />
                                </Col>
                            </Row>
                        </Card>

                        {!vistaIndividual && (
                            <Card bordered={false} styles={{ body: { padding: '16px' } }} style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                                <Title level={5} style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#475569' }}>Distribución de Ventas</Title>
                                {datosDona.length === 0 ? <Empty description="Sin datos" /> : (
                                    <div style={{ height: '240px' }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie data={datosDona} cx="50%" cy="45%" innerRadius={55} outerRadius={85} paddingAngle={2} dataKey="value" stroke="none">
                                                    {datosDona.map((entry, index) => <Cell key={`cell-${index}`} fill={PALETA_COLORES[index % PALETA_COLORES.length]} />)}
                                                </Pie>
                                                <Tooltip formatter={(value) => `$${Number(value).toLocaleString('es-CO')}`} />
                                                <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: '11px' }} />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                )}
                            </Card>
                        )}
                    </Space>
                </Col>

                {/* COLUMNA DERECHA: TABLA Y FILTROS */}
                <Col xs={24} xl={17} xxl={18}>
                    <Card bordered={false} style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', height: '100%' }} styles={{ body: { padding: '0' } }}>

                        {/* Cabecera integrada (Oculta al imprimir) */}
                        <div className="no-print" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap', gap: '10px' }}>
                            <Title level={5} style={{ margin: 0, fontSize: '15px' }}>
                                {vistaIndividual ? `Perfil de Rendimiento: ${empleadoInfo.nombre_completo}` : 'Ranking de Clasificación (Leaderboard)'}
                            </Title>
                            <Space>
                                <Select
                                    value={empleadoSeleccionado}
                                    onChange={(val) => { setEmpleadoSeleccionado(val); setExportScope(val === 'todos' ? 'todos' : 'individual'); }}
                                    style={{ width: 220 }}
                                    options={opcionesSelect}
                                    showSearch optionFilterProp="label"
                                />
                                <Button type="primary" icon={<DownloadOutlined />} onClick={() => setIsExportModalOpen(true)} style={{ background: '#22BABB' }}>
                                    Exportar
                                </Button>
                            </Space>
                        </div>

                        {/* Título solo visible al imprimir */}
                        <div style={{ display: 'none', padding: '20px' }} className="print-only">
                            <Title level={4}>Reporte de Rendimiento de Personal</Title>
                            <Text>Rango: {dayjs(desde).format('DD/MM/YYYY')} al {dayjs(hasta).format('DD/MM/YYYY')}</Text>
                        </div>

                        {/* Tabla de Alto Rendimiento */}
                        <Table
                            dataSource={rankingMostrado}
                            rowKey="id"
                            pagination={vistaIndividual ? false : { pageSize: 8 }}
                            scroll={{ x: 'max-content' }}
                            size="middle"
                            expandable={{
                                expandedRowRender: (record) => (
                                    <div style={{ margin: 0, padding: '8px 16px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                        <Text strong style={{ fontSize: '12px', color: '#475569', display: 'block', marginBottom: '6px' }}>
                                            🏆 Top Productos Más Vendidos por {record.nombre_completo}:
                                        </Text>
                                        {record.top_productos && record.top_productos.length > 0 ? (
                                            <Space size={[8, 8]} wrap>
                                                {record.top_productos.map((prod, idx) => (
                                                    <Tag key={idx} color="cyan" style={{ margin: 0, padding: '4px 10px', fontSize: '12px', fontWeight: '500' }}>
                                                        #{idx + 1} {prod.nombre} — <b>{prod.unidades} unds</b>
                                                    </Tag>
                                                ))}
                                            </Space>
                                        ) : (
                                            <Text type="secondary" style={{ fontSize: '12px' }}>No hay registro de productos detallados para este periodo.</Text>
                                        )}
                                    </div>
                                ),
                                rowExpandable: (record) => record.top_productos && record.top_productos.length > 0,
                            }}
                            columns={[
                                { title: 'Pos', width: 60, align: 'center', render: (_, __, index) => (index === 0 && !vistaIndividual ? <CrownFilled style={{ color: '#f59e0b', fontSize: '18px' }} /> : <Text type="secondary" strong>#{index + 1}</Text>) },
                                {
                                    title: 'Empleado', dataIndex: 'nombre_completo', render: (text, record, index) => (
                                        <Space><Avatar size="small" style={{ backgroundColor: PALETA_COLORES[index % PALETA_COLORES.length] }}>{text.charAt(0).toUpperCase()}</Avatar><Text strong style={{ color: '#334155' }}>{text}</Text></Space>
                                    )
                                },
                                {
                                    title: (
                                        <Space>
                                            Aporte
                                            <AntTooltip title="Porcentaje que este empleado aporta a las ventas totales de la empresa." color="#0f172a">
                                                <InfoCircleOutlined style={{ color: '#94a3b8' }} />
                                            </AntTooltip>
                                        </Space>
                                    ),
                                    width: 200,
                                    render: (_, record, index) => {
                                        const mostrarPorcentaje = record.porcentaje_aporte > 0 && record.porcentaje_aporte < 1
                                            ? '< 1%'
                                            : `${record.porcentaje_aporte}%`;

                                        return (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <div style={{ flex: 1 }}>
                                                    <Progress
                                                        percent={record.porcentaje_aporte}
                                                        strokeColor={PALETA_COLORES[index % PALETA_COLORES.length]}
                                                        size="small"
                                                        showInfo={false}
                                                    />
                                                </div>
                                                <Text type="secondary" style={{ fontSize: '12px', minWidth: '35px' }}>
                                                    {mostrarPorcentaje}
                                                </Text>
                                            </div>
                                        )
                                    }
                                },
                                { title: 'Venta Total', dataIndex: 'total_vendido', align: 'right', render: (val) => <Text strong style={{ color: '#059669', fontSize: '14px' }}>${Number(val).toLocaleString('es-CO')}</Text> },
                                { title: 'Facturas', dataIndex: 'total_facturas', align: 'center', render: (val) => <Tag style={{ margin: 0, border: 'none', background: '#f1f5f9', color: '#475569' }}>{val}</Tag> },
                                {
                                    title: (
                                        <Space>
                                            Ticket Prom.
                                            <AntTooltip title="Dinero promedio que gasta un cliente cada vez que este cajero lo atiende. (Total vendido dividido entre sus facturas)." color="#0f172a">
                                                <InfoCircleOutlined style={{ color: '#94a3b8' }} />
                                            </AntTooltip>
                                        </Space>
                                    ),
                                    dataIndex: 'ticket_promedio',
                                    align: 'right',
                                    render: (val) => <Text type="secondary">${Number(val).toLocaleString('es-CO')}</Text>
                                },
                                {
                                    title: (
                                        <Space>
                                            Devoluc.
                                            <AntTooltip title="Cantidad de notas de crédito o devoluciones generadas a partir de las ventas de este empleado." color="#0f172a">
                                                <InfoCircleOutlined style={{ color: '#94a3b8' }} />
                                            </AntTooltip>
                                        </Space>
                                    ),
                                    dataIndex: 'devoluciones',
                                    align: 'center',
                                    render: (val) => val > 0 ? <Tag color="error" style={{ margin: 0 }}>{val}</Tag> : <Text type="secondary">-</Text>
                                },
                                {
                                    title: 'Análisis',
                                    align: 'center',
                                    render: (_, record) => (
                                        <Button 
                                            type="primary" 
                                            size="small" 
                                            // Si no tienes ZoomInOutlined importado, puedes usar EyeOutlined o InfoCircleOutlined
                                            icon={<EyeOutlined />} 
                                            style={{ backgroundColor: '#0f172a', borderRadius: '6px' }}
                                            onClick={() => setEmpleadoAnalizar(record)}
                                        >
                                            Ver Detalles
                                        </Button>
                                    )
                                }
                            ]}
                        />
                    </Card>
                </Col>
            </Row>

            {/* ========================================== */}
            {/* MODAL INTELIGENTE DE EXPORTACIÓN           */}
            {/* ========================================== */}
            <Modal
                title={<Title level={4} style={{ margin: 0 }}>Exportar Reporte de Personal</Title>}
                open={isExportModalOpen}
                onCancel={() => setIsExportModalOpen(false)}
                footer={null}
                centered
                width={480}
            >
                {/* Selección de Formato */}
                <div style={{ marginBottom: '24px', marginTop: '16px' }}>
                    <Text strong style={{ fontSize: '14px', display: 'block', marginBottom: '12px' }}>1. Selecciona el formato del archivo:</Text>
                    <Radio.Group
                        onChange={e => setExportFormat(e.target.value)}
                        value={exportFormat}
                        style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}
                    >
                        <Radio value="xlsx">
                            <Space><FileExcelOutlined style={{ color: '#10b981', fontSize: '18px' }} /> <Text><b>Excel Corporativo (.xlsx)</b> - Ideal para auditorías y contabilidad.</Text></Space>
                        </Radio>
                        <Radio value="csv">
                            <Space><FileTextOutlined style={{ color: '#64748b', fontSize: '18px' }} /> <Text><b>CSV Texto Plano (.csv)</b> - Ideal para migración de datos.</Text></Space>
                        </Radio>
                        <Radio value="pdf">
                            <Space><PrinterOutlined style={{ color: '#ef4444', fontSize: '18px' }} /> <Text><b>Impresión / PDF (.pdf)</b> - Ideal para presentar en reuniones.</Text></Space>
                        </Radio>
                    </Radio.Group>
                </div>

                {/* Selección de Alcance (Filtro) */}
                <div style={{ marginBottom: '28px', background: '#f8fafc', padding: '16px', borderRadius: '8px' }}>
                    <Text strong style={{ fontSize: '14px', display: 'block', marginBottom: '12px' }}>2. ¿Qué información deseas incluir?</Text>
                    <Radio.Group
                        onChange={e => setExportScope(e.target.value)}
                        value={exportScope}
                        style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
                    >
                        <Radio value="todos">Equipo Completo (Todos los empleados)</Radio>
                        <Radio value="individual" disabled={empleadoSeleccionado === 'todos'}>
                            Solo el empleado filtrado en pantalla
                            {empleadoSeleccionado !== 'todos' && <span style={{ color: '#22BABB', fontWeight: 'bold' }}> ({empleadoInfo?.nombre_completo})</span>}
                        </Radio>
                    </Radio.Group>
                </div>

                <Button
                    type="primary" block size="large" icon={<DownloadOutlined />}
                    style={{ background: '#0f172a', borderRadius: '8px', fontWeight: 'bold', height: '45px' }}
                    onClick={handleExport}
                >
                    GENERAR Y DESCARGAR
                </Button>
            </Modal>

            {empleadoAnalizar && (
                <ModalAnalisisVendedor
                    visible={!!empleadoAnalizar}
                    onClose={() => setEmpleadoAnalizar(null)}
                    empleado={empleadoAnalizar}
                    desde={desde}
                    hasta={hasta}
                />
            )}
        </div>
    );
};

export default SeccionPersonal;